import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Box from "@mui/material/Box";

import { withPartial } from "./granularity/granularity";

export type MilestoneBarDatum = { key: string; label: string; value: number; side: "before" | "after"; partial?: boolean };
export type MilestoneMarker = { atKey: string; text: string };
export type MilestoneBarsProps = { data: MilestoneBarDatum[]; markers?: MilestoneMarker[]; ariaLabel: string; height?: number; width?: number };

export const SIDE_COLOR = { before: "#94a3b8", after: "#05bbe5" } as const;
const MARKER_COLOR = "#7c5cd6";
const PIN_R = 8;
const PIN_STEP = PIN_R * 2 + 2;
const ROW_STEP = PIN_R * 2 + 2;

export type MarkerPlacement = { marker: MilestoneMarker; number: number; dx: number; row: number };

/**
 * Com 2+ marcos o rótulo vira um pino numerado (texto na legenda abaixo do gráfico). Marcos na mesma
 * semana se espalham na horizontal (como o `pinOffset` do ComboChart); marcos em semanas vizinhas
 * alternam a altura, para os pinos nunca se sobreporem.
 */
export function placeMarkers(markers: MilestoneMarker[], keys: string[]): MarkerPlacement[] {
  const indexOf = (k: string) => keys.indexOf(k);
  const ordered = markers
    .map((m, i) => ({ m, i, at: indexOf(m.atKey) }))
    .filter((x) => x.at >= 0)
    .sort((a, b) => a.at - b.at || a.i - b.i);
  const perKey = new Map<string, number>();
  ordered.forEach(({ m }) => perKey.set(m.atKey, (perKey.get(m.atKey) ?? 0) + 1));
  const seen = new Map<string, number>();
  const out: MarkerPlacement[] = [];
  ordered.forEach(({ m, at }, n) => {
    const slot = seen.get(m.atKey) ?? 0;
    seen.set(m.atKey, slot + 1);
    const of = perKey.get(m.atKey) ?? 1;
    const prev = n > 0 ? out[n - 1] : null;
    const prevAt = n > 0 ? ordered[n - 1].at : -Infinity;
    const row = prev && at - prevAt <= 1 ? 1 - prev.row : 0;
    out.push({ marker: m, number: n + 1, dx: (slot - (of - 1) / 2) * PIN_STEP, row });
  });
  return out;
}

type PinProps = { viewBox?: { x: number; y: number }; placement: MarkerPlacement };

function MarkerPin({ viewBox, placement }: PinProps) {
  const cx = (viewBox?.x ?? 0) + placement.dx;
  const cy = (viewBox?.y ?? 0) - PIN_R - 2 - placement.row * ROW_STEP;
  return (
    <g role="img" aria-label={placement.marker.text} data-testid="milestone-pin">
      <title>{placement.marker.text}</title>
      <circle cx={cx} cy={cy} r={PIN_R} fill={MARKER_COLOR} stroke="#fff" strokeWidth={1.5} />
      <text x={cx} y={cy} dy="0.35em" textAnchor="middle" fill="#fff" fontSize={10} fontWeight={600}>
        {placement.number}
      </text>
    </g>
  );
}

/** Série semanal antes × depois de um marco (detalhe do ponto e drill do motivo). */
export default function MilestoneBars({ data, markers = [], ariaLabel, height = 180, width }: MilestoneBarsProps) {
  const labelOf = new Map(data.map((d) => [d.key, d.label]));
  const partialOf = new Set(data.filter((d) => d.partial).map((d) => d.key));
  const numbered = markers.length > 1;
  const placements = numbered ? placeMarkers(markers, data.map((d) => d.key)) : [];
  const rows = placements.some((p) => p.row === 1) ? 2 : 1;
  const top = numbered ? PIN_R * 2 + 6 + (rows - 1) * ROW_STEP : 22;
  const chart = (
    <BarChart data={data} width={width} height={height} margin={{ top, right: 8, bottom: 0, left: 0 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="key" tickFormatter={(k: string) => labelOf.get(k) ?? k} tick={{ fontSize: 11 }} />
      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
      <Tooltip labelFormatter={(k: string) => withPartial(labelOf.get(k) ?? k, partialOf.has(k))} formatter={(v: number) => [v, "Feedbacks"]} />
      <Bar dataKey="value" isAnimationActive={false}>
        {data.map((d) => (
          <Cell key={d.key} fill={SIDE_COLOR[d.side]} fillOpacity={d.partial ? 0.45 : 1} />
        ))}
      </Bar>
      {numbered
        ? placements.map((p) => (
            <ReferenceLine key={`${p.marker.atKey}-${p.number}`} x={p.marker.atKey} stroke={MARKER_COLOR} strokeDasharray="4 4" label={<MarkerPin placement={p} />} />
          ))
        : markers.map((m) => (
            <ReferenceLine key={`${m.atKey}-${m.text}`} x={m.atKey} stroke={MARKER_COLOR} strokeDasharray="4 4" label={{ value: m.text, position: "top", fill: MARKER_COLOR, fontSize: 11 }} />
          ))}
    </BarChart>
  );
  return (
    <Box>
      <Box role="img" aria-label={ariaLabel}>
        {width ? chart : <ResponsiveContainer width="100%" height={height}>{chart}</ResponsiveContainer>}
      </Box>
      {numbered && placements.length > 0 && (
        <Box component="ol" aria-label="Marcos no gráfico" sx={{ listStyle: "none", m: 0, mt: 0.5, p: 0, display: "flex", flexWrap: "wrap", gap: 0.5, columnGap: 1.5 }}>
          {placements.map((p) => (
            <Box component="li" key={`${p.marker.atKey}-${p.number}`} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 11, color: MARKER_COLOR }}>
              <Box component="span" aria-hidden sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: MARKER_COLOR, color: "#fff", fontSize: 10, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {p.number}
              </Box>
              <span>{p.marker.text}</span>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
